"use client";

import {
  createContext,
  useContext,
  useState,
  useEffect,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { synchronizeProblemCache } from "@/lib/problemCacheSync";

type ProblemCacheState = "checking" | "ready" | "bypass";
const CacheReadyContext = createContext<ProblemCacheState>("ready");

export const PROBLEM_CACHE_SYNC_SOFT_TIMEOUT_MS = 300;

export function useProblemCacheState() {
  return useContext(CacheReadyContext);
}

export function ProblemCacheInvalidator({
  children,
}: {
  children: ReactNode;
}) {
  // 짧은 시간은 캐시 동기화를 기다려 IndexedDB 적중률을 유지한다. 제한 시간을 넘기면
  // 문제 조회만 서버 우회로 열고, 동기화 자체는 백그라운드에서 계속 진행한다.
  const [cacheState, setCacheState] = useState<ProblemCacheState>("checking");
  const queryClient = useQueryClient();

  useEffect(() => {
    let active = true;
    const bypassTimer = window.setTimeout(() => {
      if (active) {
        setCacheState((current) =>
          current === "checking" ? "bypass" : current,
        );
      }
    }, PROBLEM_CACHE_SYNC_SOFT_TIMEOUT_MS);

    async function check() {
      try {
        await synchronizeProblemCache(queryClient);
        if (active) setCacheState("ready");
      } catch (e) {
        console.warn("[CacheInvalidator] 체크 실패:", e);
        // 변경 여부를 확인하지 못한 캐시는 계속 우회한다.
        if (active) setCacheState("bypass");
      } finally {
        window.clearTimeout(bypassTimer);
      }
    }
    void check();
    return () => {
      active = false;
      window.clearTimeout(bypassTimer);
    };
  }, [queryClient]);

  return (
    <CacheReadyContext.Provider value={cacheState}>
      {children}
    </CacheReadyContext.Provider>
  );
}
